import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { membershipService } from '../../services/membershipService';
import { MembershipPlan, CreateMembershipPlanRequest } from '../../types/membership';
import MembershipPlanForm from './MembershipPlanForm';

const MembershipPlansTab: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<MembershipPlan | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState<MembershipPlan | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const queryClient = useQueryClient();

  const { data: plansData, isLoading, error } = useQuery({
    queryKey: ['membershipPlans'],
    queryFn: () => membershipService.getMembershipPlans(),
  });

  const createPlanMutation = useMutation({
    mutationFn: (data: CreateMembershipPlanRequest) => membershipService.createMembershipPlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['membershipPlans'] });
      setOpenDialog(false);
      setSelectedPlan(null);
      setSnackbar({ open: true, message: 'Membership plan created successfully', severity: 'success' });
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.message || 'Failed to create plan', severity: 'error' });
    },
  });

  const updatePlanMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateMembershipPlanRequest> }) =>
      membershipService.updateMembershipPlan(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['membershipPlans'] });
      setOpenDialog(false);
      setSelectedPlan(null);
      setSnackbar({ open: true, message: 'Membership plan updated successfully', severity: 'success' });
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.message || 'Failed to update plan', severity: 'error' });
    },
  });

  const deletePlanMutation = useMutation({
    mutationFn: (id: string) => membershipService.deleteMembershipPlan(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['membershipPlans'] });
      setDeleteDialogOpen(false);
      setPlanToDelete(null);
      setSnackbar({ open: true, message: 'Membership plan deleted successfully', severity: 'success' });
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.message || 'Failed to delete plan', severity: 'error' });
    },
  });

  const togglePlanStatusMutation = useMutation({
    mutationFn: ({ id, activate }: { id: string; activate: boolean }) =>
      activate ? membershipService.activateMembershipPlan(id) : membershipService.deactivateMembershipPlan(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['membershipPlans'] });
      setSnackbar({
        open: true,
        message: 'Membership plan status updated successfully',
        severity: 'success'
      });
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.message || 'Failed to update plan status', severity: 'error' });
    },
  });

  const handleCreatePlan = () => {
    setSelectedPlan(null);
    setOpenDialog(true);
  };

  const handleEditPlan = (plan: MembershipPlan) => {
    setSelectedPlan(plan);
    setOpenDialog(true);
  };

  const handleDeletePlan = (plan: MembershipPlan) => {
    setPlanToDelete(plan);
    setDeleteDialogOpen(true);
  };

  const handleTogglePlanStatus = (plan: MembershipPlan) => {
    togglePlanStatusMutation.mutate({ id: plan.id, activate: !plan.isActive });
  };

  const handleFormSubmit = (data: CreateMembershipPlanRequest) => {
    if (selectedPlan) {
      updatePlanMutation.mutate({ id: selectedPlan.id, data });
    } else {
      createPlanMutation.mutate(data);
    }
  };

  const confirmDelete = () => {
    if (planToDelete) {
      deletePlanMutation.mutate(planToDelete.id);
    }
  };

  const getPlanTypeColor = (type: string) => {
    switch (type) {
      case 'TIERED': return 'primary';
      case 'CREDIT_BASED': return 'secondary';
      case 'ALL_INCLUSIVE': return 'success';
      case 'PREPAID': return 'warning';
      default: return 'default';
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(price);
  };

  const formatDuration = (duration: number, unit: string) => {
    return `${duration} ${unit.toLowerCase()}${duration > 1 ? 's' : ''}`;
  };

  if (isLoading) {
    return <Typography>Loading membership plans...</Typography>;
  }

  if (error) {
    return <Alert severity="error">Failed to load membership plans</Alert>;
  }

  const plans = (plansData as any)?.data?.plans || (plansData as any)?.plans || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Membership Plans</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreatePlan}
        >
          Create Plan
        </Button>
      </Box>

      <Grid container spacing={3}>
        {plans.map((plan: MembershipPlan) => (
          <Grid item xs={12} sm={6} md={4} key={plan.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" component="h3">
                    {plan.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={plan.type.replace('_', ' ')}
                      color={getPlanTypeColor(plan.type) as any}
                      size="small"
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleTogglePlanStatus(plan)}
                      color={plan.isActive ? 'success' : 'default'}
                    >
                      {plan.isActive ? <ViewIcon /> : <VisibilityOffIcon />}
                    </IconButton>
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {plan.description}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
                    {formatPrice(plan.price)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    for {formatDuration(plan.duration, plan.durationUnit)}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Status: {plan.isActive ? 'Active' : 'Inactive'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Created: {new Date(plan.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>

                {plan.features && Object.keys(plan.features).length > 0 && (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                      Features:
                    </Typography>
                    {Object.entries(plan.features).slice(0, 3).map(([key, value]) => (
                      <Typography key={key} variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        • {key}: {String(value)}
                      </Typography>
                    ))}
                    {Object.keys(plan.features).length > 3 && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        +{Object.keys(plan.features).length - 3} more features
                      </Typography>
                    )}
                  </Box>
                )}
              </CardContent>

              <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => handleEditPlan(plan)}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleDeletePlan(plan)}
                >
                  Delete
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {plans.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No membership plans found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first membership plan to get started
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreatePlan}>
            Create Plan
          </Button>
        </Box>
      )}

      {/* Create/Edit Plan Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedPlan ? 'Edit Membership Plan' : 'Create Membership Plan'}
        </DialogTitle>
        <DialogContent>
          <MembershipPlanForm
            initialData={selectedPlan}
            onSubmit={handleFormSubmit}
            onCancel={() => setOpenDialog(false)}
            isLoading={createPlanMutation.isPending || updatePlanMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Membership Plan</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the membership plan "{planToDelete?.name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={deletePlanMutation.isPending}
          >
            {deletePlanMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MembershipPlansTab;